import { redirect } from "@tanstack/react-router";
import { useAuthStore } from "@/stores/auth";
import { useInstanceStore } from "@/stores/instance";
import { safeReturnUrl } from "@/lib/oauth";

/**
 * Route guards. These run in beforeLoad and must not throw for expected
 * redirects (TanStack Router treats thrown redirect() as navigation).
 */

interface GuardLocation {
  pathname: string;
  /** Search may be a parsed object; we only need it for the return URL. */
  search?: unknown;
}

function toSearchString(search: unknown): string {
  if (!search) return "";
  if (typeof search === "string") return search;
  try {
    const params = new URLSearchParams(search as Record<string, string>);
    const s = params.toString();
    return s ? `?${s}` : "";
  } catch {
    return "";
  }
}

async function ensureBooted(): Promise<void> {
  const instance = useInstanceStore.getState();
  if (instance.status === "idle") await instance.load();
  const auth = useAuthStore.getState();
  if (auth.status === "unknown") await auth.restore();
}

/** Redirect needsSetup instances to /auth/signup (first-admin creation). */
function redirectIfNeedsSetup(currentPath: string): void {
  const { profile } = useInstanceStore.getState();
  if (profile?.needsSetup && currentPath !== "/auth/signup") {
    throw redirect({ to: "/auth/signup", replace: true });
  }
}

/** Require an authenticated user; bounce to /auth with a safe return URL. */
export async function requireAuth(ctx: { location: GuardLocation }): Promise<void> {
  await ensureBooted();
  redirectIfNeedsSetup(ctx.location.pathname);
  const { status, user } = useAuthStore.getState();
  if (status !== "authenticated" || !user) {
    const returnTo = safeReturnUrl(ctx.location.pathname + toSearchString(ctx.location.search), "/");
    throw redirect({
      to: "/auth",
      search: { redirect: returnTo },
      replace: true,
    });
  }
}

/** Require NO authenticated user (auth pages); bounce signed-in users home. */
export async function requireGuest(): Promise<void> {
  await ensureBooted();
  const { status } = useAuthStore.getState();
  if (status === "authenticated") {
    throw redirect({ to: "/", replace: true });
  }
}

/** Load boot data without any auth redirect (public pages). */
export async function loadPublic(ctx?: { location: GuardLocation }): Promise<void> {
  await ensureBooted();
  redirectIfNeedsSetup(ctx?.location.pathname ?? window.location.pathname);
}
